'use client'

import * as React from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { motion, type HTMLMotionProps } from 'motion/react'
import { cn } from '../cn'

type CheckboxProps = React.ComponentProps<typeof CheckboxPrimitive.Root> &
  HTMLMotionProps<'button'> & {
    /** Extra classes on the checkmark SVG (round-radio sizing). */
    indicatorClassName?: string
    /** Stroke width for the check path — round radios use a thinner stroke. */
    indicatorStrokeWidth?: number
    /** Override path / viewBox (round radios use a circle-centered mark). */
    indicatorPath?: string
    indicatorViewBox?: string
    /** Keep checked on re-click (wizard round radios). */
    preventUncheck?: boolean
  }

const DEFAULT_CHECK_PATH = 'M4.5 12.75l6 6 9-13.5'
const DEFAULT_CHECK_VIEWBOX = '0 0 24 24'

function Checkbox({
  className,
  indicatorClassName,
  indicatorStrokeWidth = 3.5,
  indicatorPath = DEFAULT_CHECK_PATH,
  indicatorViewBox = DEFAULT_CHECK_VIEWBOX,
  preventUncheck = false,
  checked,
  defaultChecked,
  disabled,
  onClick,
  onCheckedChange,
  whileHover,
  whileTap,
  ...rootProps
}: CheckboxProps) {
  const isControlled = checked !== undefined
  const [isChecked, setIsChecked] = React.useState(
    checked ?? defaultChecked ?? false
  )
  const visualState = isControlled ? checked : isChecked
  const checkedVisual = visualState === true || visualState === 'indeterminate'
  const indeterminate = visualState === 'indeterminate'

  const handleCheckedChange = React.useCallback(
    (next: boolean | 'indeterminate') => {
      if (!isControlled) setIsChecked(next)
      onCheckedChange?.(next)
    },
    [isControlled, onCheckedChange]
  )

  return (
    <CheckboxPrimitive.Root
      checked={checked}
      defaultChecked={defaultChecked}
      disabled={disabled}
      {...rootProps}
      onCheckedChange={handleCheckedChange}
      asChild
    >
      <motion.button
        type='button'
        data-slot='checkbox'
        disabled={disabled}
        className={cn(
          'peer border-input bg-background focus-visible:ring-ring data-[state=checked]:border-primary data-[state=checked]:bg-primary data-[state=checked]:text-primary-foreground flex size-5 shrink-0 items-center justify-center rounded-[var(--radius-checkbox)] border transition-colors duration-150 focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50',
          className
        )}
        whileTap={whileTap ?? { scale: 0.95 }}
        whileHover={whileHover ?? { scale: 1.05 }}
        onClick={onClick}
        onPointerDown={(e) => {
          if (preventUncheck && checked === true && e.button === 0) {
            e.preventDefault()
          }
        }}
      >
        <CheckboxPrimitive.Indicator forceMount asChild>
          <motion.svg
            data-slot='checkbox-indicator'
            xmlns='http://www.w3.org/2000/svg'
            fill='none'
            viewBox={indeterminate ? DEFAULT_CHECK_VIEWBOX : indicatorViewBox}
            className={cn('size-3.5', indicatorClassName)}
            initial={false}
            animate={checkedVisual ? 'checked' : 'unchecked'}
          >
            <motion.path
              stroke='currentColor'
              strokeWidth={indicatorStrokeWidth}
              strokeLinecap='round'
              strokeLinejoin='round'
              d={indeterminate ? 'M5 12h14' : indicatorPath}
              variants={{
                checked: {
                  pathLength: 1,
                  opacity: 1,
                  transition: {
                    duration: 0.12,
                    ease: [0.23, 1, 0.32, 1],
                  },
                },
                unchecked: {
                  pathLength: 0,
                  opacity: 0,
                  transition: {
                    opacity: { duration: 0.06, ease: [0.4, 0, 1, 1] },
                    pathLength: { duration: 0.1, ease: [0.4, 0, 1, 1] },
                  },
                },
              }}
            />
          </motion.svg>
        </CheckboxPrimitive.Indicator>
      </motion.button>
    </CheckboxPrimitive.Root>
  )
}

export { Checkbox, type CheckboxProps }
